import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Type, Palette, Ruler, Save, RotateCcw, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const TypographySettings = ({ i18n }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [settings, setSettings] = useState({
    // Heading Sizes
    h1Size: '3.5rem',
    h2Size: '2.5rem',
    h3Size: '2rem',
    h4Size: '1.5rem',
    h5Size: '1.25rem',
    h6Size: '1rem',
    
    // Body Text
    bodySize: '1rem',
    smallTextSize: '0.875rem',
    
    // Line Heights
    headingLineHeight: '1.2',
    bodyLineHeight: '1.6',
    
    // Font Weights
    headingWeight: '700',
    bodyWeight: '400',
    boldWeight: '600',
    
    // Font Families
    headingFont: 'system-ui',
    bodyFont: 'system-ui',
    
    // Colors
    primaryTextColor: '#0f172a',
    secondaryTextColor: '#64748b',
    accentColor: '#10b981',
    
    // Spacing
    sectionSpacing: '4rem',
    elementSpacing: '1.5rem',
    
    // Border Radius
    borderRadius: '0.75rem',
    buttonRadius: '0.5rem'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/typography-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/typography-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save settings');
      }

      toast.success(i18n.language === 'ar' ? 'تم حفظ الإعدادات! ✅' : 'Settings saved! ✅');
      
      // Apply settings to CSS variables
      applySettingsToCSS();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(i18n.language === 'ar' ? `فشل حفظ الإعدادات: ${error.message}` : `Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const applySettingsToCSS = () => {
    const root = document.documentElement;
    root.style.setProperty('--heading-font', settings.headingFont);
    root.style.setProperty('--body-font', settings.bodyFont);
    root.style.setProperty('--primary-text', settings.primaryTextColor);
    root.style.setProperty('--secondary-text', settings.secondaryTextColor);
    root.style.setProperty('--accent-color', settings.accentColor);
  };

  const handleReset = () => {
    loadSettings();
    toast.info(i18n.language === 'ar' ? 'تم إعادة تعيين الإعدادات' : 'Settings reset');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Type className="w-7 h-7 text-slate-600" />
          {i18n.language === 'ar' ? 'إعدادات التصميم والنصوص' : 'Typography & Design Settings'}
        </h3>
        <Button
          variant="outline"
          onClick={() => setShowPreview(!showPreview)}
          className="gap-2"
        >
          <Eye className="w-4 h-4" />
          {showPreview 
            ? (i18n.language === 'ar' ? 'إخفاء المعاينة' : 'Hide Preview')
            : (i18n.language === 'ar' ? 'معاينة' : 'Preview')
          }
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          {/* Heading Sizes */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Type className="w-5 h-5 text-blue-600" />
              <h4 className="text-lg font-bold text-slate-900">
                {i18n.language === 'ar' ? 'أحجام العناوين' : 'Heading Sizes'}
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">H1 {i18n.language === 'ar' ? 'حجم' : 'Size'}</Label>
                <Input
                  value={settings.h1Size}
                  onChange={(e) => setSettings({ ...settings, h1Size: e.target.value })}
                  placeholder="3.5rem"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">H2 {i18n.language === 'ar' ? 'حجم' : 'Size'}</Label>
                <Input
                  value={settings.h2Size}
                  onChange={(e) => setSettings({ ...settings, h2Size: e.target.value })}
                  placeholder="2.5rem"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">H3 {i18n.language === 'ar' ? 'حجم' : 'Size'}</Label>
                <Input
                  value={settings.h3Size}
                  onChange={(e) => setSettings({ ...settings, h3Size: e.target.value })}
                  placeholder="2rem"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">H4 {i18n.language === 'ar' ? 'حجم' : 'Size'}</Label>
                <Input
                  value={settings.h4Size}
                  onChange={(e) => setSettings({ ...settings, h4Size: e.target.value })}
                  placeholder="1.5rem"
                  className="mt-1"
                />
              </div>
            </div>
          </Card>

          {/* Body Text */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Ruler className="w-5 h-5 text-green-600" />
              <h4 className="text-lg font-bold text-slate-900">
                {i18n.language === 'ar' ? 'النصوص الأساسية' : 'Body Text'}
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">{i18n.language === 'ar' ? 'حجم النص الأساسي' : 'Body Text Size'}</Label>
                <Input
                  value={settings.bodySize}
                  onChange={(e) => setSettings({ ...settings, bodySize: e.target.value })}
                  placeholder="1rem"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">{i18n.language === 'ar' ? 'النص الصغير' : 'Small Text'}</Label>
                <Input
                  value={settings.smallTextSize}
                  onChange={(e) => setSettings({ ...settings, smallTextSize: e.target.value })}
                  placeholder="0.875rem"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">{i18n.language === 'ar' ? 'ارتفاع السطر' : 'Line Height'}</Label>
                <Input
                  value={settings.bodyLineHeight}
                  onChange={(e) => setSettings({ ...settings, bodyLineHeight: e.target.value })}
                  placeholder="1.6"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">{i18n.language === 'ar' ? 'وزن الخط' : 'Font Weight'}</Label>
                <Select value={settings.bodyWeight} onValueChange={(val) => setSettings({ ...settings, bodyWeight: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="300">Light (300)</SelectItem>
                    <SelectItem value="400">Normal (400)</SelectItem>
                    <SelectItem value="500">Medium (500)</SelectItem>
                    <SelectItem value="600">Semibold (600)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Colors */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-5 h-5 text-purple-600" />
              <h4 className="text-lg font-bold text-slate-900">
                {i18n.language === 'ar' ? 'الألوان' : 'Colors'}
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">{i18n.language === 'ar' ? 'لون النص الأساسي' : 'Primary Text'}</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={settings.primaryTextColor}
                    onChange={(e) => setSettings({ ...settings, primaryTextColor: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={settings.primaryTextColor}
                    onChange={(e) => setSettings({ ...settings, primaryTextColor: e.target.value })}
                    placeholder="#0f172a"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">{i18n.language === 'ar' ? 'لون النص الثانوي' : 'Secondary Text'}</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={settings.secondaryTextColor}
                    onChange={(e) => setSettings({ ...settings, secondaryTextColor: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={settings.secondaryTextColor}
                    onChange={(e) => setSettings({ ...settings, secondaryTextColor: e.target.value })}
                    placeholder="#64748b"
                  />
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-sm">{i18n.language === 'ar' ? 'اللون المميز' : 'Accent Color'}</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={settings.accentColor}
                    onChange={(e) => setSettings({ ...settings, accentColor: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={settings.accentColor}
                    onChange={(e) => setSettings({ ...settings, accentColor: e.target.value })}
                    placeholder="#10b981"
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Column - Preview */}
        {showPreview && (
          <Card className="p-6 sticky top-4">
            <h4 className="text-lg font-bold text-slate-900 mb-4">
              {i18n.language === 'ar' ? 'المعاينة' : 'Preview'}
            </h4>
            <div className="space-y-4">
              <h1 style={{ fontSize: settings.h1Size, color: settings.primaryTextColor, fontWeight: settings.headingWeight }}>
                Heading 1
              </h1>
              <h2 style={{ fontSize: settings.h2Size, color: settings.primaryTextColor, fontWeight: settings.headingWeight }}>
                Heading 2
              </h2>
              <h3 style={{ fontSize: settings.h3Size, color: settings.primaryTextColor, fontWeight: settings.headingWeight }}>
                Heading 3
              </h3>
              <p style={{ fontSize: settings.bodySize, lineHeight: settings.bodyLineHeight, color: settings.primaryTextColor }}>
                This is body text. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </p>
              <p style={{ fontSize: settings.smallTextSize, color: settings.secondaryTextColor }}>
                This is small text for captions and meta information.
              </p>
              <Button style={{ backgroundColor: settings.accentColor, borderRadius: settings.buttonRadius }}>
                {i18n.language === 'ar' ? 'زر تجريبي' : 'Sample Button'}
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 border-t border-slate-200 pt-6">
        <Button variant="outline" onClick={handleReset} disabled={saving}>
          <RotateCcw className="w-4 h-4 me-2" />
          {i18n.language === 'ar' ? 'إعادة تعيين' : 'Reset'}
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 me-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'حفظ الإعدادات' : 'Save Settings'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default TypographySettings;
